package modysy.model

import modysy.datastructures.Vector2

import math.{cos, sin}

/** Represents a rotation combined with translation in 2D space.
  */
class Transform(var translation: Vector2, var orientation: Double) {

  /** Transform the coordinates of the supplied vector.
    *
    * @param point A vector to be transformed.
    * @return The result of first rotating the vector counter-clockwise by orientation radians, and then adding
    *         translation to it.
    */
  def *(point: Vector2): Vector2 =
    Vector2(
      cos(orientation) * point.x - sin(orientation) * point.y + translation.x,
      sin(orientation) * point.x + cos(orientation) * point.y + translation.y
    )

  def *(other: Transform): Transform =
    Transform
      .rotate(orientation + other.orientation)
      .translate(
        (
          other.translation
            .dot(cos(orientation), -sin(orientation)) + translation.x,
          other.translation
            .dot(sin(orientation), cos(orientation)) + translation.y
        )
      )

  /** Add additional rotation to the transformation in-place.
    *
    * Notice this only modifies the rotational part of this transformation. It does not calculate a transformation
    * that first rotates by initial rotation, then translates by initial translation, and then rotates by supplied
    * angle on top of it.
    *
    * @return Return this for convenience.
    * */
  def rotate(angle: Double): Transform = {
    orientation += angle
    this
  }

  /** Add additional translation to the transformation in-place.
    *
    * Because translation is applied after rotation, this is the same as calculation a transformation that first
    * rotates by initial rotation, then translates by initial translation, and then translates by additional
    * translation.
    *
    * @return Return this for convenience.
    * */
  def translate(additionalTranslation: Vector2): Transform = {
    translation += additionalTranslation
    this
  }

  /** Return a new transformation that has the same translation but zero rotation. */
  def translationPart: Transform = new Transform(translation, 0)

  /** Return a new transformation that has the same rotation but zero translation. */
  def orientationPart: Transform = new Transform((0, 0), orientation)

  /** Return a new transformation that is the inverse of this one. */
  def inverse: Transform = {
    // Source: analytical solution of R(orientation) * [ix, iy] = [-tx, -ty]
    val inverseX = translation.dot(-cos(orientation), -sin(orientation))
    val inverseY = translation.dot(sin(orientation), -cos(orientation))

    new Transform((inverseX, inverseY), -orientation)
  }

}

object Transform {

  /** Create an identity transformation (no translation and no rotation). */
  def identity: Transform = new Transform((0, 0), 0)

  /** Create a transformation that has no translation and a rotation. */
  def rotate(angle: Double): Transform = identity.rotate(angle)

  /** Create a transformation that has no rotation and a translation. */
  def translate(translation: Vector2): Transform =
    identity.translate(translation)
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/model/Transform.scala